import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';
import {
  registerDocumentText,
  type FiscalBlockView,
  type PrintedElement,
  type PrintedReceipt,
} from '@/lib/receipt-document';
import {
  parseRegisterMarkup,
  registerQrPayload,
  type RegisterFont,
  type RegisterLine,
} from '@/lib/register-markup';
import { QrCode } from './qr-code';

/**
 * PFC-21 — the stored fiscal block of a receipt and the receipt as it was
 * printed. Shared by the receipt detail page and the fiscal cabinet's
 * electronic journal, so both show one document the same way.
 *
 * Country-neutral on purpose: the fields are what any fiscal adapter reports
 * (document number, fiscal number, time, signature, QR payload); the words on
 * the printed receipt come from the till and are shown verbatim.
 */

function FiscalRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 flex-1 break-all', mono === true && 'font-mono text-xs')}>
        {value}
      </dd>
    </div>
  );
}

/**
 * `showQrImage`: false when the printed receipt below already draws this QR
 * code; the panel then gives its content as text, so the page has one image.
 */
export function FiscalBlockPanel({
  block,
  showQrImage = true,
}: {
  block: FiscalBlockView;
  showQrImage?: boolean;
}) {
  const t = useT('admin_ui');
  return (
    <section
      className="space-y-2 rounded-md border border-border bg-card p-4 text-sm"
      data-testid="fiscal-block"
    >
      <header className="flex items-center gap-2">
        <h2 className="text-sm font-medium">{t('receipts.fiscal.title', 'Fiscal block')}</h2>
        {block.testDocument && (
          <Badge variant="destructive">
            {t('receipts.fiscal.testDocument', 'Test document — no fiscal effect')}
          </Badge>
        )}
      </header>
      <dl className="space-y-1">
        {block.receiptNumber !== null && (
          <FiscalRow
            label={t('receipts.fiscal.receiptNumber', 'Receipt number')}
            value={block.receiptNumber}
            mono
          />
        )}
        {block.fiscalNumber !== null && (
          <FiscalRow
            label={t('receipts.fiscal.fiscalNumber', 'Fiscal number')}
            value={block.fiscalNumber}
            mono
          />
        )}
        {block.registrarId !== null && (
          <FiscalRow
            label={
              block.registrarLabel ?? t('receipts.fiscal.registrarId', 'Register fiscal number')
            }
            value={block.registrarId}
            mono
          />
        )}
        {block.timestampFormatted !== null && (
          <FiscalRow
            label={t('receipts.fiscal.timestamp', 'Fiscal time')}
            value={block.timestampFormatted}
            mono
          />
        )}
        {/* A UA PRRO till sends the DPS fiscal number as the signature (the
            DSTU signature proper stays in its journal), so the row would only
            repeat the fiscal number. */}
        {block.signature !== null && block.signature !== block.fiscalNumber && (
          <FiscalRow
            label={t('receipts.fiscal.signature', 'Signature / control value')}
            value={block.signature}
            mono
          />
        )}
        {block.qrPayload !== null && (
          <FiscalRow
            label={t('receipts.fiscal.qrCode', 'QR code')}
            mono={!showQrImage}
            value={
              showQrImage ? (
                <QrCode
                  value={block.qrPayload}
                  label={t('receipts.fiscal.qrCode', 'QR code')}
                  align="start"
                />
              ) : (
                block.qrPayload
              )
            }
          />
        )}
      </dl>
    </section>
  );
}

function Pair({ left, right }: { left: string; right: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="min-w-0 break-words">{left}</span>
      <span className="shrink-0 text-right">{right}</span>
    </div>
  );
}

function Element({ element }: { element: PrintedElement }) {
  const t = useT('admin_ui');
  switch (element.kind) {
    case 'header':
      return (
        <div className="text-center">
          {element.retailerName !== null && (
            <div className="font-semibold">{element.retailerName}</div>
          )}
          {element.lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      );
    case 'item_line':
      return (
        <div>
          <div className="break-words">
            {element.sku !== null ? `${element.sku} ` : ''}
            {element.name}
          </div>
          <Pair
            left={`${element.quantityFormatted}${
              element.unitPriceFormatted !== null ? ` × ${element.unitPriceFormatted}` : ''
            }${element.vatRateFormatted !== null ? ` · ${element.vatRateFormatted}` : ''}`}
            right={element.lineTotalFormatted}
          />
          {element.discountFormatted !== null && (
            <Pair
              left={t('receipts.detail.discount', 'Discount')}
              right={element.discountFormatted}
            />
          )}
        </div>
      );
    case 'totals_block':
      return (
        <div className="border-t border-dashed border-border pt-1">
          {element.subtotalFormatted !== null && (
            <Pair
              left={t('receipts.detail.subtotal', 'Subtotal')}
              right={element.subtotalFormatted}
            />
          )}
          {element.discountFormatted !== null && (
            <Pair
              left={t('receipts.detail.discount', 'Discount')}
              right={element.discountFormatted}
            />
          )}
          {element.vatBreakdown.map((v, i) => (
            <Pair key={i} left={`${v.rateFormatted} (${v.baseFormatted})`} right={v.vatFormatted} />
          ))}
          {element.totalFormatted !== null && (
            <div className="font-semibold">
              <Pair left={t('receipts.field.total', 'Total')} right={element.totalFormatted} />
            </div>
          )}
        </div>
      );
    case 'fiscal_block': {
      const b = element.block;
      return (
        <div className="border-t border-dashed border-border pt-1">
          {b.testDocument && (
            <div className="text-center font-semibold">
              {element.marking ??
                t('receipts.fiscal.testDocument', 'Test document — no fiscal effect')}
            </div>
          )}
          {b.receiptNumber !== null && (
            <Pair
              left={t('receipts.fiscal.receiptNumber', 'Receipt number')}
              right={b.receiptNumber}
            />
          )}
          {b.fiscalNumber !== null && (
            <Pair
              left={t('receipts.fiscal.fiscalNumber', 'Fiscal number')}
              right={b.fiscalNumber}
            />
          )}
          {b.registrarId !== null && (
            <Pair
              left={b.registrarLabel ?? t('receipts.fiscal.registrarId', 'Register fiscal number')}
              right={b.registrarId}
            />
          )}
          {b.timestampFormatted !== null && <div>{b.timestampFormatted}</div>}
          {b.qrPayload !== null && (
            <QrCode
              value={b.qrPayload}
              label={t('receipts.fiscal.qrCode', 'QR code')}
              className="pt-1"
            />
          )}
        </div>
      );
    }
    case 'footer':
      return (
        <div className="text-center">
          {element.retailerFooterText !== null && <div>{element.retailerFooterText}</div>}
          {element.lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      );
    case 'legal_text':
      return <div className="whitespace-pre-wrap text-center">{element.text}</div>;
    case 'register_document':
      // Only reached when `PrintedReceiptView` has not already shown the
      // register's text on its own (it always has); kept for completeness.
      return <pre className="whitespace-pre font-mono">{element.text}</pre>;
    case 'unknown':
      return (
        <div className="text-muted-foreground">
          [{element.type}] {element.text.join(' · ')}
        </div>
      );
  }
}

/** How an `ESC f` font reads on screen, keeping the register's columns. */
const REGISTER_FONT_CLASS: Record<RegisterFont, string> = {
  normal: '',
  // The register lays double-width text out at half the paper width: each
  // character takes two columns, so the figures still line up on the right.
  double_width: 'font-semibold tracking-[1ch]',
  double_height: 'font-bold',
  double: 'font-bold tracking-[1ch]',
  bold: 'font-bold',
};

/**
 * The register's own receipt, laid out as it printed: its lines in its
 * spacing (no wrapping; columns are aligned with spaces), its fonts read from
 * the engine's `ESC f` selectors and the QR drawn where the engine printed it.
 */
function RegisterDocument({ lines, qrLabel }: { lines: RegisterLine[]; qrLabel: string }) {
  return (
    <div className="font-mono text-xs leading-snug">
      {lines.map((line, i) =>
        line.kind === 'qr' ? (
          <QrCode key={i} value={line.payload} label={qrLabel} className="my-2" />
        ) : (
          <div
            key={i}
            className={cn('min-h-[1lh] whitespace-pre', line.vendorMark && 'text-center')}
            data-testid="register-line"
          >
            {line.segments.map((segment, j) => (
              <span key={j} className={REGISTER_FONT_CLASS[segment.font]}>
                {segment.text}
              </span>
            ))}
          </div>
        ),
      )}
    </div>
  );
}

/**
 * The register's text in its paper-width box: scrolls sideways when wider
 * than the screen, and draws `fallbackQr` under the text when the text has no
 * QR command of its own.
 */
function RegisterTextBox({
  lines,
  fallbackQr,
  qrLabel,
  ariaLabel,
}: {
  lines: RegisterLine[];
  fallbackQr: string | null;
  qrLabel: string;
  ariaLabel: string;
}) {
  return (
    <div
      className="mx-auto w-fit max-w-full overflow-x-auto rounded-sm border border-dashed border-border bg-background p-4"
      data-testid="register-document"
      // Wide register text scrolls sideways: reachable from the keyboard.
      role="region"
      tabIndex={0}
      aria-label={ariaLabel}
    >
      <RegisterDocument lines={lines} qrLabel={qrLabel} />
      {registerQrPayload(lines) === null && fallbackQr !== null && (
        <QrCode value={fallbackQr} label={qrLabel} />
      )}
    </div>
  );
}

/**
 * PFC-24 — a register document other than a receipt (an X or Z report, a
 * service cash in / out) exactly as the register printed it: the same reading
 * of the engine's print markup as a receipt's `register_document`, with the
 * document's QR in its printed place (or under the text when the text has no
 * QR command and `qrPayload` is given).
 */
export function RegisterTextView({
  text,
  title,
  qrPayload = null,
}: {
  text: string;
  title: string;
  qrPayload?: string | null;
}) {
  const t = useT('admin_ui');
  return (
    <section className="rounded-md border border-border bg-card" data-testid="register-text">
      <header className="border-b border-border px-4 py-2 text-sm font-medium">{title}</header>
      <div className="p-4">
        <RegisterTextBox
          lines={parseRegisterMarkup(text)}
          fallbackQr={qrPayload}
          qrLabel={t('receipts.fiscal.qrCode', 'QR code')}
          ariaLabel={title}
        />
      </div>
    </section>
  );
}

/**
 * The QR content the printed receipt view draws, or null when it draws none:
 * with the register's text, `qrPayload` (else the printed fiscal block's);
 * with Back Office's elements, the printed fiscal block's.
 */
export function printedQrPayload(
  receipt: PrintedReceipt,
  qrPayload: string | null | undefined,
): string | null {
  const printedBlock = receipt.elements.find(
    (e): e is Extract<PrintedElement, { kind: 'fiscal_block' }> => e.kind === 'fiscal_block',
  )?.block.qrPayload;
  const registerText = registerDocumentText(receipt);
  if (registerText !== null) {
    return (
      registerQrPayload(parseRegisterMarkup(registerText)) ?? qrPayload ?? printedBlock ?? null
    );
  }
  return printedBlock ?? null;
}

/**
 * The receipt as printed. When the register laid the receipt out itself (a
 * `register_document`, the PRRO fiscal library's text), that text is the
 * receipt and is shown alone — exactly as printed, line breaks and spacing
 * kept — with the QR code under it, as on paper. Back Office's own elements
 * are rendered only for a till that sent nothing else.
 *
 * `qrPayload` is the receipt's fiscal block QR content (the stored block,
 * completed from the printed one); without it the printed fiscal block's is
 * used.
 */
export function PrintedReceiptView({
  receipt,
  qrPayload,
}: {
  receipt: PrintedReceipt;
  qrPayload?: string | null;
}) {
  const t = useT('admin_ui');
  const registerText = registerDocumentText(receipt);
  const registerLines = registerText === null ? null : parseRegisterMarkup(registerText);
  const qr = printedQrPayload(receipt, qrPayload);
  const qrLabel = t('receipts.fiscal.qrCode', 'QR code');
  return (
    <section className="rounded-md border border-border bg-card" data-testid="printed-receipt">
      <header className="border-b border-border px-4 py-2 text-sm font-medium">
        {t('receipts.printed.title', 'Printed receipt')}
      </header>
      <div className="p-4">
        {registerLines !== null ? (
          // A register text without its own QR command: the fiscal block's QR under it.
          <RegisterTextBox
            lines={registerLines}
            fallbackQr={qr}
            qrLabel={qrLabel}
            ariaLabel={t('receipts.printed.title', 'Printed receipt')}
          />
        ) : (
          <div className="mx-auto max-w-sm space-y-2 rounded-sm border border-dashed border-border bg-background p-4 font-mono text-xs">
            {receipt.elements.map((element, i) => (
              <Element key={i} element={element} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
